import React, { useState, useEffect, useMemo } from 'react';
import { Upload, FileText, Download, Eye, AlertCircle, Save, Trash2, History, RefreshCw, Calendar } from 'lucide-react';
import { useApp } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Finanzas = () => {
  const { finanzasHistorial, setFinanzasHistorial } = useApp();
  const [xmlData, setXmlData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [xmlPreview, setXmlPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Filtrar historial por fechas
  const historialFiltrado = useMemo(() => {
    if (!fechaDesde && !fechaHasta) return historial;
    return historial.filter(item => {
      const fecha = item.fecha || '';
      if (fechaDesde && fecha < fechaDesde) return false;
      if (fechaHasta && fecha > fechaHasta) return false;
      return true;
    });
  }, [historial, fechaDesde, fechaHasta]);

  // Usar datos precargados del contexto al montar
  useEffect(() => {
    if (finanzasHistorial && finanzasHistorial.length > 0) {
      setHistorial(finanzasHistorial);
    } else {
      fetchHistorial();
    }
  }, []);

  const fetchHistorial = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/historial`);
      const data = await response.json();
      if (data.success) {
        setHistorial(data.historial || []);
        setFinanzasHistorial(data.historial || []);
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
    setLoading(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setError('');
    setXmlData([]);
    setDocumentInfo(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        setXmlPreview(text.substring(0, 3000));

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');

        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          setError('Error al parsear el XML. Verifica que el archivo sea válido.');
          return;
        }

        const docInfo = extractDocumentInfo(xmlDoc);
        setDocumentInfo(docInfo);

        const items = extractDataFromXML(xmlDoc, docInfo);

        if (items.length === 0) {
          setError('No se encontraron líneas de detalle en el documento.');
        } else {
          setXmlData(items);
        }
      } catch (err) {
        setError('Error al procesar el archivo: ' + err.message);
      }
    };

    reader.onerror = () => {
      setError('Error al leer el archivo');
    };

    reader.readAsText(file);
  };

  const extractDocumentInfo = (xmlDoc) => {
    const getValueNS = (tagName) => {
      const element = xmlDoc.getElementsByTagNameNS('*', tagName)[0];
      return element ? element.textContent.trim() : null;
    };

    // Buscar el número de factura real (no el CUFE)
    // Primero intentar ParentDocumentID que contiene el número visible de la factura
    let numeroFactura = getValueNS('ParentDocumentID');
    
    // Si no está, buscar en el ID del SenderParty o similar
    if (!numeroFactura) {
      // Buscar en el documento embebido si existe
      const attachmentElements = xmlDoc.getElementsByTagNameNS('*', 'Attachment');
      if (attachmentElements.length > 0) {
        const externalRef = attachmentElements[0].getElementsByTagNameNS('*', 'ExternalReference')[0];
        if (externalRef) {
          const description = externalRef.getElementsByTagNameNS('*', 'Description')[0];
          if (description) {
            const embeddedXML = description.textContent;
            try {
              let xmlText = embeddedXML;
              if (!embeddedXML.trim().startsWith('<')) {
                xmlText = atob(embeddedXML);
              }
              const parser = new DOMParser();
              const invoiceDoc = parser.parseFromString(xmlText, 'text/xml');
              // Buscar el ID dentro del documento embebido (factura real)
              const idElement = invoiceDoc.getElementsByTagNameNS('*', 'ID')[0];
              if (idElement) {
                numeroFactura = idElement.textContent.trim();
              }
            } catch (e) {
              console.log('No se pudo extraer número de factura del XML embebido:', e);
            }
          }
        }
      }
    }
    
    // Si aún no tenemos número, usar el ID principal pero solo si parece ser un número de factura corto
    if (!numeroFactura) {
      const idPrincipal = getValueNS('ID');
      // Si el ID es muy largo (CUFE tiene más de 50 caracteres), no usarlo
      if (idPrincipal && idPrincipal.length < 50) {
        numeroFactura = idPrincipal;
      }
    }

    return {
      numero: numeroFactura || 'Sin número',
      fecha: getValueNS('IssueDate'),
      tipoDocumento: xmlDoc.documentElement.localName || 'Desconocido'
    };
  };

  const extractDataFromXML = (xmlDoc, docInfo) => {
    const items = [];
    let invoiceDoc = xmlDoc;

    const attachmentElements = xmlDoc.getElementsByTagNameNS('*', 'Attachment');
    if (attachmentElements.length > 0) {
      const externalRef = attachmentElements[0].getElementsByTagNameNS('*', 'ExternalReference')[0];
      if (externalRef) {
        const description = externalRef.getElementsByTagNameNS('*', 'Description')[0];
        if (description) {
          const embeddedXML = description.textContent;
          try {
            let xmlText = embeddedXML;
            if (!embeddedXML.trim().startsWith('<')) {
              xmlText = atob(embeddedXML);
            }
            const parser = new DOMParser();
            invoiceDoc = parser.parseFromString(xmlText, 'text/xml');
          } catch (e) {
            console.log('No se pudo extraer XML embebido:', e);
          }
        }
      }
    }

    const invoiceLines = invoiceDoc.getElementsByTagNameNS('*', 'InvoiceLine');

    if (invoiceLines.length === 0) {
      const creditLines = invoiceDoc.getElementsByTagNameNS('*', 'CreditNoteLine');
      const debitLines = invoiceDoc.getElementsByTagNameNS('*', 'DebitNoteLine');

      if (creditLines.length > 0) {
        return processLines(creditLines, invoiceDoc, docInfo);
      } else if (debitLines.length > 0) {
        return processLines(debitLines, invoiceDoc, docInfo);
      }
    } else {
      return processLines(invoiceLines, invoiceDoc, docInfo);
    }

    return items;
  };

  const getValueFromElementNS = (element, tagName) => {
    const found = element.getElementsByTagNameNS('*', tagName)[0];
    return found ? found.textContent.trim() : null;
  };

  const getValueFromDocNS = (doc, tagName) => {
    const found = doc.getElementsByTagNameNS('*', tagName)[0];
    return found ? found.textContent.trim() : null;
  };

  const processLines = (lines, xmlDoc, docInfo) => {
    const items = [];
    const docDate = getValueFromDocNS(xmlDoc, 'IssueDate');
    
    // Usar el número de factura ya extraído por extractDocumentInfo
    // Si no está disponible, buscar el ID del documento interno (factura real, no el AttachedDocument)
    let numeroFactura = docInfo?.numero;
    
    // Si el número viene del docInfo y parece ser un CUFE (muy largo), buscar el ID real
    if (!numeroFactura || numeroFactura.length > 50) {
      const idElement = xmlDoc.getElementsByTagNameNS('*', 'ID')[0];
      if (idElement) {
        const idValue = idElement.textContent.trim();
        if (idValue.length < 50) {
          numeroFactura = idValue;
        }
      }
    }
    
    numeroFactura = numeroFactura || '-';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      const cantidad = getValueFromElementNS(line, 'InvoicedQuantity') ||
                      getValueFromElementNS(line, 'CreditedQuantity') ||
                      getValueFromElementNS(line, 'DebitedQuantity') ||
                      getValueFromElementNS(line, 'Quantity') || '1';

      const itemElement = line.getElementsByTagNameNS('*', 'Item')[0];
      let descripcion = '-';
      let codigoProducto = '-';
      if (itemElement) {
        descripcion = getValueFromElementNS(itemElement, 'Description') ||
                     getValueFromElementNS(itemElement, 'Name') || '-';

        const sellersItemId = getValueFromElementNS(itemElement, 'SellersItemIdentification');
        const standardItemId = getValueFromElementNS(itemElement, 'StandardItemIdentification');

        if (sellersItemId) {
          const sellerIdElements = itemElement.getElementsByTagNameNS('*', 'SellersItemIdentification');
          if (sellerIdElements.length > 0) {
            codigoProducto = getValueFromElementNS(sellerIdElements[0], 'ID') || '-';
          }
        } else if (standardItemId) {
          const standardIdElements = itemElement.getElementsByTagNameNS('*', 'StandardItemIdentification');
          if (standardIdElements.length > 0) {
            codigoProducto = getValueFromElementNS(standardIdElements[0], 'ID') || '-';
          }
        }
      }

      const priceElement = line.getElementsByTagNameNS('*', 'Price')[0];
      let vrUnitario = '-';
      if (priceElement) {
        vrUnitario = getValueFromElementNS(priceElement, 'PriceAmount');
      }

      const valorBruto = getValueFromElementNS(line, 'LineExtensionAmount');

      items.push({
        fecha: docDate || '-',
        numeroFactura: numeroFactura,
        codigoProducto: codigoProducto,
        descripcion: descripcion,
        cantidad: cantidad || '1',
        vrUnitario: vrUnitario || '-',
        valorBruto: valorBruto || '-'
      });
    }

    return items;
  };

  const handleSaveToHistorial = async () => {
    if (xmlData.length === 0) return;
    
    setSaving(true);
    try {
      const itemsToSave = xmlData.map(item => ({
        fecha: item.fecha,
        numero_factura: item.numeroFactura,
        codigo_producto: item.codigoProducto,
        cantidad: parseFloat(item.cantidad) || 1,
        valor_bruto: parseFloat(item.valorBruto) || 0
      }));

      const response = await fetch(`${API_URL}/api/finanzas/historial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          archivo: fileName,
          items: itemsToSave
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ ${data.message}`);
        fetchHistorial();
        // Limpiar datos del XML después de guardar
        setXmlData([]);
        setFileName('');
        setDocumentInfo(null);
        setXmlPreview('');
      } else {
        alert(`❌ Error: ${data.error}`);
      }
    } catch (err) {
      alert(`❌ Error al guardar: ${err.message}`);
    }
    setSaving(false);
  };

  const handleClearHistorial = async () => {
    if (!window.confirm('¿Estás seguro de eliminar todo el historial?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/historial`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setHistorial([]);
        alert('✅ Historial eliminado');
      }
    } catch (err) {
      console.error('Error limpiando historial:', err);
      alert('❌ Error al limpiar historial');
    }
  };

  // Función para eliminar un ítem individual del historial
  const handleDeleteHistorialItem = async (itemId, codigoProducto) => {
    if (!window.confirm(`¿Estás seguro de eliminar el registro del producto "${codigoProducto}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/historial/${itemId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        // Actualizar el estado local eliminando el ítem
        setHistorial(prev => prev.filter(item => item.item_id !== itemId));
        alert('✅ Registro eliminado');
      } else {
        alert('❌ Error: ' + (data.error || 'No se pudo eliminar'));
      }
    } catch (err) {
      console.error('Error eliminando registro:', err);
      alert('❌ Error al eliminar registro');
    }
  };

  const exportToCSV = () => {
    if (xmlData.length === 0) return;

    const headers = ['Fecha', 'Código Producto', 'Descripción', 'Cantidad', 'Vr. Unitario', 'Valor Bruto'];
    const csvContent = [
      headers.join(','),
      ...xmlData.map(row => [
        row.fecha,
        row.codigoProducto,
        `"${row.descripcion.replace(/"/g, '""')}"`,
        row.cantidad,
        row.vrUnitario,
        row.valorBruto
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `datos_${fileName.replace('.xml', '')}.csv`;
    link.click();
  };

  const exportHistorialToCSV = () => {
    if (historialFiltrado.length === 0) return;

    const headers = ['Fecha', 'Número Factura', 'Código', 'Costo', 'Ventas', 'Ganancias', 'Porcentaje'];
    const csvContent = [
      headers.join(','),
      ...historialFiltrado.map(row => [
        row.fecha,
        row.numero_factura,
        row.codigo_producto,
        row.costo_total,
        row.valor_bruto,
        row.ganancia,
        `${row.porcentaje}%`
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historial_finanzas_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2
    }).format(num);
  };

  // Calcular totales del historial filtrado
  const totales = historialFiltrado.reduce((acc, item) => ({
    costo: acc.costo + (parseFloat(item.costo_total) || 0),
    ventas: acc.ventas + (parseFloat(item.valor_bruto) || 0),
    ganancia: acc.ganancia + (parseFloat(item.ganancia) || 0)
  }), { costo: 0, ventas: 0, ganancia: 0 });

  const porcentajeTotal = totales.ventas > 0 ? (totales.ganancia / totales.ventas * 100).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">Finanzas - Lector XML DIAN</h1>
            </div>
            <button
              onClick={() => setShowHistorial(!showHistorial)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showHistorial ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
              data-testid="toggle-historial"
            >
              <History className="w-4 h-4" />
              {showHistorial ? 'Ocultar Historial' : 'Ver Historial'}
            </button>
          </div>

          {/* Área de carga */}
          <div className="mb-8">
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-indigo-300 rounded-lg cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-12 h-12 text-indigo-500 mb-3" />
                <p className="mb-2 text-sm text-gray-600">
                  <span className="font-semibold">Click para cargar</span> o arrastra el archivo
                </p>
                <p className="text-xs text-gray-500">Archivos XML (Facturas, Notas Crédito/Débito DIAN)</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".xml"
                onChange={handleFileUpload}
                data-testid="xml-file-input"
              />
            </label>
            {fileName && (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Archivo: <span className="font-semibold">{fileName}</span>
                </p>
                <div className="flex gap-2">
                  {xmlPreview && (
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      {showPreview ? 'Ocultar XML' : 'Ver XML'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Información del documento */}
          {documentInfo && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold mb-2 text-blue-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Información del Documento
              </h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-700 font-medium">Tipo:</span>
                  <span className="ml-2 text-blue-900">{documentInfo.tipoDocumento}</span>
                </div>
                {documentInfo.numero && (
                  <div>
                    <span className="text-blue-700 font-medium">Número:</span>
                    <span className="ml-2 text-blue-900">{documentInfo.numero}</span>
                  </div>
                )}
                {documentInfo.fecha && (
                  <div>
                    <span className="text-blue-700 font-medium">Fecha:</span>
                    <span className="ml-2 text-blue-900">{documentInfo.fecha}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preview del XML */}
          {showPreview && xmlPreview && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold mb-2 text-gray-700">Vista previa del XML:</h3>
              <pre className="text-xs overflow-x-auto bg-gray-900 text-green-400 p-3 rounded max-h-96">
                {xmlPreview}...
              </pre>
              <p className="text-xs text-gray-500 mt-2">Mostrando primeros 3000 caracteres</p>
            </div>
          )}

          {/* Mensajes de error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>{error}</div>
              </div>
            </div>
          )}

          {/* Tabla de datos del XML cargado */}
          {xmlData.length > 0 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  Líneas del archivo ({xmlData.length} {xmlData.length !== 1 ? 'registros' : 'registro'})
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveToHistorial}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    data-testid="save-to-historial"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Guardando...' : 'Guardar en Historial'}
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Exportar CSV
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-indigo-600 text-white">
                      <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                      <th className="px-4 py-3 text-left font-semibold">N° Factura</th>
                      <th className="px-4 py-3 text-left font-semibold">Código</th>
                      <th className="px-4 py-3 text-left font-semibold">Descripción</th>
                      <th className="px-4 py-3 text-right font-semibold">Cantidad</th>
                      <th className="px-4 py-3 text-right font-semibold">Valor Bruto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {xmlData.map((row, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-50'}
                      >
                        <td className="px-4 py-3 border-b border-gray-200">{row.fecha}</td>
                        <td className="px-4 py-3 border-b border-gray-200 font-mono text-sm">{row.numeroFactura}</td>
                        <td className="px-4 py-3 border-b border-gray-200 font-mono text-sm">{row.codigoProducto}</td>
                        <td className="px-4 py-3 border-b border-gray-200 max-w-xs truncate">{row.descripcion}</td>
                        <td className="px-4 py-3 border-b border-gray-200 text-right font-mono">{row.cantidad}</td>
                        <td className="px-4 py-3 border-b border-gray-200 text-right font-mono font-semibold">{formatCurrency(row.valorBruto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Información inicial */}
          {xmlData.length === 0 && !error && !fileName && (
            <div className="text-center text-gray-500 py-8">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">Carga un archivo XML para comenzar</p>
              <p className="text-sm">Compatible con facturas electrónicas DIAN formato UBL</p>
            </div>
          )}

          {/* HISTORIAL */}
          {showHistorial && (
            <div className="mt-8 pt-8 border-t-2 border-indigo-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <History className="w-6 h-6 text-indigo-600" />
                  Historial de Finanzas
                  <span className="text-sm font-normal text-gray-500 ml-2">({historialFiltrado.length} registros)</span>
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={fetchHistorial}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </button>
                  <button
                    onClick={exportHistorialToCSV}
                    disabled={historialFiltrado.length === 0}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    Exportar
                  </button>
                  <button
                    onClick={handleClearHistorial}
                    disabled={historial.length === 0}
                    className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Filtro por fechas */}
              <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="date-filter">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Desde:</label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="fecha-desde"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Hasta:</label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="fecha-hasta"
                  />
                </div>
                {(fechaDesde || fechaHasta) && (
                  <button
                    onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Limpiar filtro
                  </button>
                )}
              </div>

              {/* Resumen de totales */}
              {historial.length > 0 && (
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Total Costos</p>
                    <p className="text-xl font-bold text-blue-800">{formatCurrency(totales.costo)}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium">Total Ventas</p>
                    <p className="text-xl font-bold text-purple-800">{formatCurrency(totales.ventas)}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Total Ganancias</p>
                    <p className="text-xl font-bold text-green-800">{formatCurrency(totales.ganancia)}</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-600 font-medium">% Rentabilidad</p>
                    <p className="text-xl font-bold text-orange-800">{porcentajeTotal}%</p>
                  </div>
                </div>
              )}

              {historialFiltrado.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full border-collapse" data-testid="historial-table">
                    <thead>
                      <tr className="bg-indigo-800 text-white">
                        <th className="px-4 py-3 text-left font-semibold">FECHA</th>
                        <th className="px-4 py-3 text-left font-semibold">NÚMERO DE FACTURA</th>
                        <th className="px-4 py-3 text-left font-semibold">CÓDIGO</th>
                        <th className="px-4 py-3 text-right font-semibold">COSTO</th>
                        <th className="px-4 py-3 text-right font-semibold">VENTAS</th>
                        <th className="px-4 py-3 text-right font-semibold">GANANCIAS</th>
                        <th className="px-4 py-3 text-right font-semibold">PORCENTAJE</th>
                        <th className="px-4 py-3 text-center font-semibold">ACCIONES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historialFiltrado.map((row, index) => (
                        <tr
                          key={row.item_id || index}
                          className={index % 2 === 0 ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white hover:bg-gray-50'}
                        >
                          <td className="px-4 py-3 border-b border-gray-200">{row.fecha}</td>
                          <td className="px-4 py-3 border-b border-gray-200 font-mono text-sm">{row.numero_factura}</td>
                          <td className="px-4 py-3 border-b border-gray-200 font-mono text-sm font-semibold text-indigo-700">{row.codigo_producto}</td>
                          <td className="px-4 py-3 border-b border-gray-200 text-right font-mono">{formatCurrency(row.costo_total)}</td>
                          <td className="px-4 py-3 border-b border-gray-200 text-right font-mono">{formatCurrency(row.valor_bruto)}</td>
                          <td className={`px-4 py-3 border-b border-gray-200 text-right font-mono font-semibold ${
                            row.ganancia > 0 ? 'text-green-600' : row.ganancia < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {formatCurrency(row.ganancia)}
                          </td>
                          <td className={`px-4 py-3 border-b border-gray-200 text-right font-mono font-semibold ${
                            row.porcentaje > 0 ? 'text-green-600' : row.porcentaje < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {row.porcentaje?.toFixed(2)}%
                          </td>
                          <td className="px-4 py-3 border-b border-gray-200 text-center">
                            <button
                              onClick={() => handleDeleteHistorialItem(row.item_id, row.codigo_producto)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar este registro"
                              data-testid={`delete-item-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg">
                  <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">No hay registros en el historial</p>
                  <p className="text-sm">Carga un archivo XML y guárdalo para ver el historial aquí</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Finanzas;
